<template>
  <ValidationObserver @submit="addAndEditSectionHandler">
    <UiModalContainer
      :modal-show="modal"
      :modal-width="50"
      footer-classes="flex justify-end gap-3"
      @handleClick="handleClick"
    >
      <template v-slot:header>
        <span v-if="edit" v-i18n="dashboard">EDIT_{{ sectionNameSingular }}</span>
        <span v-else v-i18n="dashboard">ADD_{{ sectionNameSingular }}</span>
      </template>
      <template v-slot>
        <InputFieldWrapper>
          <UiInput
            v-model="classData.className"
            type="text"
            :title="`${sectionNameSingular}_TITLE`"
            :placeholder="`ADD_${sectionNameSingular}_TITLE`"
            rules="required"
          />
          <UiInput
            v-model="classData.classDescription"
            type="text"
            name="Description"
            :title="`${sectionNameSingular}_DESCRIPTION`"
            label="Description"
            :placeholder="`ADD_${sectionNameSingular}_DESCRIPTION`"
          />
          <UiSingleSelect
            v-model="classData.selectedClass"
            :title="classNameSingular"
            label="title"
            :options="classScopeList"
            :disabled="!!currentClassScope"
            :search-through-api="true"
            reduce="id"
            :filter="FILTERS_LIST.CLASS"
            rules="required"
            @search="getClasses"
          />
        </InputFieldWrapper>
      </template>
      <template v-slot:footer>
        <div class="flex gap-3 rtl:gap-x-4">
          <UIButton :disabled="isLoading" @click="handleClick('close')">Cancel</UIButton>
          <UIButton button="primary">
            <template v-if="isLoading">
              <Loader />
            </template>
            <template v-else>
              {{ primaryButtonText }}
            </template>
          </UIButton>
        </div>
      </template>
    </UiModalContainer>
  </ValidationObserver>
</template>

<script>
import UiInput from '@components/UiElements/UiInputBox.vue'
import UiModalContainer from '@components/UiElements/UiModalContainer.vue'
import UiSingleSelect from '@/src/components/UiElements/UiSingleSelect.vue'
import Loader from '@components/BaseComponent/Loader.vue'
import { mapState, mapActions, mapGetters } from 'vuex'
import { Form as ValidationObserver } from 'vee-validate'
import UIButton from '@src/components/UiElements/UIButton.vue'
import InputFieldWrapper from '@/src/components/UiElements/InputFieldWrapper.vue'
import { transformTextToLowercase } from '@utils/generalUtil'
import { buildWhereQuery } from '@src/utils/filters/index.js'
import FILTER_KEYS, { FILTERS_LIST } from '@src/constants/filter-constants.js'

export default {
  components: {
    UiInput,
    UiModalContainer,
    UiSingleSelect,
    ValidationObserver,
    UIButton,
    Loader,
    InputFieldWrapper,
  },
  props: {
    modal: { type: Boolean, default: false },
    edit: { type: Object, default: () => {} },
    paginationLimit: {
      type: Object,
      default: () => {},
    },
  },
  emits: ['toggle'],
  data() {
    return {
      classScopeList: [],
      options: ['Select'],
      dashboard: 'dashboard',
      isActive: false,
      isOpen: false,
      FILTERS_LIST,
      classData: {
        className: '',
        classDescription: '',
        startTime: '',
        endTime: '',
        selectedClass: '',
      },
    }
  },
  computed: {
    primaryButtonText() {
      return this.edit ? 'Update' : 'Save'
    },
    ...mapState({
      isLoading: (state) => state.layout.isLoading,
      campusId: (state) => state.layout.currentCampusScope?.id || undefined,
      classesList: (state) => state.classes.classesList,
      instituteCount: (state) => state.instituteCount.instituteCount,
      currentClassScope: (state) => state.layout.currentClassScope,
      ...mapGetters('featureNaming', [
        'sectionName',
        'className',
        'sectionNameSingular',
        'classNameSingular',
      ]),
    }),
  },
  /**
   * Created Hook
   * @description Description :
   * - getting the list of classes
   * - setting the class data
   */
  created() {
    this.getClasses()
    this.isSelectedClassExist()
    if (this.edit) this.setFomData()
    if (this.currentClassScope) this.classData.selectedClass = this.currentClassScope.id
  },
  methods: {
    ...mapActions('section', ['getClassSections', 'addNewSection']),
    ...mapActions('classes', ['getUserClasses']),
    /**
     * toggle Dropdown
     * @description toggling dropdown
     */
    toggleDropdown() {
      this.isActive = !this.isActive
      this.toggleIsOpen()
    },
    setFomData() {
      this.classData.className = this.edit.title
      this.classData.classDescription = this.edit.description
      this.classData.selectedClass = this.edit.class_id
      this.classData.startTime = this.edit.start_time
      this.classData.endTime = this.edit.end_time
    },
    async getClasses(query = '') {
      let payload = {
        $where: {
          ...buildWhereQuery(FILTER_KEYS.EQUAL, 'campus_id', this.campusId),
          ...buildWhereQuery(FILTER_KEYS.ILIKE, 'title', query),
        },
      }
      this.classScopeList = await this.getUserClasses(payload)
      let existingClass = this.classScopeList?.find(
        (classItem) => classItem.id === this.edit?.class?.id,
      )
      if (this.edit && this.edit.class && !existingClass) {
        this.classScopeList.push(this.edit.class)
      }
    },
    isSelectedClassExist() {
      const isClassPresent = this.classScopeList.some(
        (classItem) => classItem.id === this.currentClassScope?.id,
      )
      if (!isClassPresent) this.classScopeList.push(this.currentClassScope)
    },
    /**
     * toggle isOpen
     * @param {Boolean} value
     * @returns {void}
     * @description toggling the value of IsOpen
     */
    toggleIsOpen(value) {
      this.isOpen = typeof value === 'boolean' ? value : !this.isOpen
    },
    /**
     * close Dropdown
     * @description closing the drop down menus
     */
    closeDropdown() {
      if (this.isActive) {
        this.isActive = false
        this.toggleIsOpen()
      }
    },
    /**
     * Handle Click
     * @param {String} eventName
     * @returns {void}
     * @description Function Description :
     * - handling click on the basis of parameter
     * - emiting toggle to parent component
     */
    handleClick(eventName) {
      switch (eventName) {
        case 'close':
          this.$emit('toggle')
          break
      }
    },
    /**
     * Form Check
     * @param {Boolean} invalid
     * @returns {void}
     * @description Function Description :
     * - checking form
     * - emiting toggle to parent component
     */
    addAndEditSectionHandler() {
      this.$store.commit('layout/IS_LOADING', true)
      const data = {
        classes: {
          title: this.classData.className,
          description: this.classData.classDescription,
          start_time: this.classData.startTime,
          end_time: this.classData.endTime,
          class_id: this.classData.selectedClass,
        },
      }
      if (this.edit) data.id = this.edit.id
      this.addNewSection(data)
        .then((res) => {
          this.$store.commit('instituteCount/INCREMENT_INSTITUTE_COUNT', 'sections_count')
          this.$store.commit(
            'toast/NEW',
            {
              type: 'success',
              message: `${res.data.title} ${transformTextToLowercase(
                this.sectionNameSingular,
              )} successfuly ${this.edit ? 'updated.' : 'created.'}`,
            },
            { root: true },
          )
          if (this.campusId) {
            this.getClassSections({
              campuseId: this.campusId,
            })
          }

          this.$emit('toggle', this.paginationLimit)
        })
        .finally(() => {
          this.$store.commit('layout/IS_LOADING', false)
        })
    },
  },
}
</script>
